import React from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { Tool, SkipForward } from "react-feather";
import StatusBadge from "../../../components/common/StatusBadge";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import {
  useGetAmcScheduleQuery,
  useCreateVisitFromScheduleMutation,
  useSkipScheduledVisitMutation,
} from "../amcApi";

const STATUS_CLASSES = {
  PLANNED: "badge-lineinfo",
  DONE: "badge-linesuccess",
  MISSED: "badge-linedanger",
  SKIPPED: "badge-linedark",
};
const STATUS_LABELS = { PLANNED: "Planned", DONE: "Done", MISSED: "Missed", SKIPPED: "Skipped" };

const fmt = (v) =>
  v
    ? new Date(`${String(v).substring(0, 10)}T00:00:00`).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

/**
 * The contract's visit calendar, generated when the contract was saved.
 *
 * Create Visit is the entire AMC-to-Technical integration: it calls the same service-visit
 * endpoint the Log Service Visit screen uses, then links the resulting job back to this row.
 * From that moment it is an ordinary service visit - assignment, QR check-in, checklist,
 * complete and close all behave exactly as they always have.
 */
const AmcScheduleSection = ({ contractRecordGuid, canManage, onHold = false }) => {
  const { data: schedule = [], isLoading } = useGetAmcScheduleQuery(
    { contractRecordGuid },
    { skip: !contractRecordGuid }
  );
  const [createVisit, { isLoading: isCreating }] = useCreateVisitFromScheduleMutation();
  const [skipVisit] = useSkipScheduledVisitMutation();

  if (!contractRecordGuid) return null;

  const today = new Date().toISOString().substring(0, 10);

  const handleCreate = async (row) => {
    try {
      const result = await createVisit({ recordGuid: row.recordGuid, visitDescription: null, technicianRecordGuids: null }).unwrap();
      toast.success(`Service visit ${result.workNumber} raised. Assign a technician from the Technical module.`);
    } catch (err) {
      toast.error(err?.data?.message || "Could not raise the service visit.");
    }
  };

  const handleSkip = async (row) => {
    const confirmed = await confirmDelete({
      title: `Skip visit ${row.visitNumber}?`,
      text: `The visit planned for ${fmt(row.plannedDate)} will be marked skipped. It stays on the record.`,
    });
    if (!confirmed) return;
    try {
      await skipVisit({ recordGuid: row.recordGuid, skipReason: "Skipped from the AMC screen" }).unwrap();
      toast.success("Visit skipped.");
    } catch (err) {
      toast.error(err?.data?.message || "Could not skip that visit.");
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="mb-1">Service Schedule</h5>
        <p className="text-muted mb-3">
          Generated from the contract dates and frequency.
          {/* Batch217 - the server refuses a visit on a held contract too. */}
          {onHold && <span className="text-warning fw-semibold"> On hold - no visits can be raised until it is released.</span>}
        </p>

        {isLoading && <p className="text-muted mb-0">Loading…</p>}

        {!isLoading && schedule.length === 0 && (
          <p className="text-muted mb-0">No visits scheduled.</p>
        )}

        {schedule.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Planned</th>
                  <th>Status</th>
                  <th>Service visit</th>
                  <th className="text-end">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row) => {
                  const isDue = row.status === "PLANNED" && String(row.plannedDate).substring(0, 10) <= today;
                  return (
                    <tr key={row.recordGuid}>
                      <td>{row.visitNumber}</td>
                      <td>
                        {fmt(row.plannedDate)}
                        {isDue && !row.technicalWorkRecordGuid && (
                          <small className="d-block text-warning">
                            due {row.daysOverdue > 0 ? `${row.daysOverdue} day${row.daysOverdue === 1 ? "" : "s"} ago` : "today"}
                          </small>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={row.status} classMap={STATUS_CLASSES} labelMap={STATUS_LABELS} />
                      </td>
                      <td>
                        {row.workNumber ? (
                          <Link to={`/technical/work`} title={row.technicalWorkStatus}>
                            {row.workNumber}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                        {row.technicalWorkStatus && (
                          <small className="d-block text-muted">{row.technicalWorkStatus.replace("_", " ").toLowerCase()}</small>
                        )}
                        {row.completedDate && <small className="d-block text-muted">done {fmt(row.completedDate)}</small>}
                      </td>
                      <td className="text-end">
                        {canManage && !onHold && row.status === "PLANNED" && !row.technicalWorkRecordGuid && (
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              type="button"
                              className={`btn btn-sm ${isDue ? "btn-primary" : "btn-outline-primary"}`}
                              onClick={() => handleCreate(row)}
                              disabled={isCreating}
                            >
                              <Tool size={13} className="me-1" />
                              Create Visit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => handleSkip(row)}
                              title="Skip this visit"
                            >
                              <SkipForward size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmcScheduleSection;
